import { FC, useEffect, useMemo, useState } from 'react';
import './leftSection.css';

import CottageOutlinedIcon from '@mui/icons-material/CottageOutlined';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import KitchenIcon from '@mui/icons-material/Kitchen';
import InventoryIcon from '@mui/icons-material/Inventory';
import TableChartIcon from '@mui/icons-material/TableChart';
import Logout from '@mui/icons-material/Logout';
import ReceiptIcon from '@mui/icons-material/Receipt';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import WestOutlinedIcon from '@mui/icons-material/WestOutlined';
import NorthOutlinedIcon from '@mui/icons-material/NorthOutlined';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PrintIcon from '@mui/icons-material/Print';
import HotelIcon from '@mui/icons-material/Hotel';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';

import { Link, useLocation } from 'react-router-dom';
import useGetUser from '../../../main/hooks/useGetUser';
import { useTranslation } from 'react-i18next';
import IUserMenu from '../../interfaces/IUserMenu';
import { useDispatch } from 'react-redux';
import onLogout from '../../store/stores/user/login.store.on-logout';
import useGetMenu from '../../hooks/useGetMenu';
import axiosInstance from '../../initializers/axios';
import NotificationIcon from '../../../pages/mainLanding/Landing/NotificationIcon';
import { ApiConstants } from '../../constants/ApiConstants';
import { buildEntityFileUrl } from '../../utils/fileUrl';
import AppPreferenceControls from '../appPreferences/AppPreferenceControls';

type DrawerStage = 0 | 1 | 2;
type DockPlacement = 'left' | 'top';

const PlaygroundMenuIcon = () => (
  <svg className="lsIcon" viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
    <path
      fill="currentColor"
      d="M12 2.7 4.8 8.8h14.4L12 2.7Zm-5.9 7.1v2.1h2v9.4h2.2v-4.1h3.4v4.1h2.2v-9.4h2V9.8H6.1Zm4.2 2.1h3.4v3.2h-3.4v-3.2Zm8.4 1.4h2.6c.8 0 1.4.6 1.4 1.4 0 2.7-1.9 5.1-4.5 5.7l-.5-2.1c1.6-.4 2.8-1.8 2.8-3.5h-1.8v-1.5ZM1.3 17.6c0-2.4 1.9-4.3 4.3-4.3h1v2.2h-1c-1.1 0-2.1.9-2.1 2.1v3.7H1.3v-3.7Z"
    />
  </svg>
);


interface Props {
  stage: DrawerStage;
  onToggleMenu: () => void;
  dockPlacement: DockPlacement;
  onSetDockPlacement: (placement: DockPlacement) => void;
  onNavigate?: () => void;
}

const normalizePath = (p?: string) => `/${(p ?? '').trim().replace(/^\/+/, '')}`.replace(/\/+/g, '/');
const menuRouteSource = (item?: Partial<IUserMenu['item']> | any) => item?.url || item?.code || '';

const menuUrlToUiRoute = (url?: string) => {
  const raw = (url ?? '').trim().toLowerCase();
  if (!raw) return '/';

  if (raw === 'dashboard') return '/dashboard-restaurant';
  if (raw === 'dashboard-restaurant') return '/dashboard-restaurant';
  if (raw === 'restaurant') return '/restaurant';
  if (raw === 'restaurant-map' || raw === 'restaurantmap') return '/restaurant-map';
  if (raw === 'hoteldashboard') return '/hotel-dashboard';
  if (raw === 'hotelinfo') return '/hotel';
  if (raw === 'hotelrooms') return '/rooms';
  if (raw === 'hotelroomtypes') return '/roomtypes';
  if (raw === 'hotelpricing') return '/hotelpricing';
  if (raw === 'bookingchannel') return '/booking-channel';
  if (raw === 'roomhandover' || raw === 'room-handover') return '/room-handover';if (raw === 'expenses') return '/expenses';
if (raw === 'expensecategories' || raw === 'expense-categories') return '/expenses';
if (raw === 'dailyinventoryclose' || raw === 'daily-inventory-close') return '/daily-inventory-close-grid';
if (raw.replace(/[\s_-]+/g, '') === 'userplaygroundinventory') return '/userplaygroundinventory';

  if (raw.includes('printers')) return '/printers';
  if (
    raw.includes('settings/integrations') ||
    raw.includes('settings/integratyions') ||
    (raw.includes('settings') && raw.includes('integrat')) ||
    raw.endsWith('/integration') ||
    raw === 'integration'
  )
    return '/integration';

  const withoutApiPrefix = raw.replace(/^\/?api\//, '');
  const noDomain = withoutApiPrefix.replace(/^[a-z]+:\/\/[^/]+/i, '');
  const cleaned = noDomain.replace(/^\/+/, '').replace(/\/+$/, '');
  if (!cleaned) return '/dashboard-restaurant';

  return normalizePath(cleaned);
};

const LeftSection: FC<Props> = ({ stage, onToggleMenu, dockPlacement, onSetDockPlacement, onNavigate }) => {
  const { i18n } = useTranslation();
  const user = useGetUser();
  const menuData = useGetMenu();
  const dispatch = useDispatch();
  const location = useLocation();

  const isIconsOnly = stage === 1;
  const isFull = stage === 2;
  const menuToggleLabel = isFull ? 'Close menu' : 'Expand menu';
  const moveMenuLeftLabel = 'Dock menu left';
  const moveMenuTopLabel = 'Dock menu top';

  const [openGroups, setOpenGroups] = useState<Record<number, boolean>>({});
  const [loadingCurrencies, setLoadingCurrencies] = useState(false);

  const loadCurrencies = async () => {
    try {
      setLoadingCurrencies(true);
      const res = await axiosInstance.post('api/currencies/load');
      alert(`Currencies loaded: ${res.data?.insertedOrUpdated ?? 0}`);
    } catch (e: any) {
      alert(e?.response?.data?.message ?? 'Failed to load currencies');
    } finally {
      setLoadingCurrencies(false);
    }
  };

  const items = useMemo(() => {
    const clone: IUserMenu[] = (menuData ?? []).map((m) => ({
      ...m,
      item: { ...m.item },
      subItems: (m.subItems ?? []).map((s: any) => ({ ...s })),
    }));

    return clone;
  }, [menuData]);

  useEffect(() => {
    setOpenGroups((prev) => {
      const next: Record<number, boolean> = {};

      items.forEach((menuItem) => {
        const id = menuItem.item.id;
        const hasSubs = (menuItem.subItems?.length || 0) > 0;
        if (!hasSubs) return;

        if (Object.prototype.hasOwnProperty.call(prev, id)) {
          next[id] = prev[id];
          return;
        }

        next[id] = hasActiveSub(menuItem);
      });

      return next;
    });
  }, [items]);

  const handleLogout = () => dispatch(onLogout());
  const labelOf = (raw: string) => {
    const value = (raw || '').trim();
    const normalized = value.toLowerCase().replace(/[\s_-]+/g, '');

    if (normalized === 'restaurantmap') {
      return i18n.t('menu.RestaurantMap', { defaultValue: 'Restaurant Map' });
    }

    if (normalized === 'restaurant') {
      return i18n.t('menu.Restaurant', { defaultValue: 'Restaurant' });
    }

    if (normalized === 'product' || normalized === 'products') {
      return i18n.t('menu.Product', { defaultValue: 'Products' });
    }

    if (normalized === 'dailyinventory') {
      return i18n.t('menu.DailyInventory', { defaultValue: 'Daily Inventory' });
    }

    if (normalized === 'hotel') {
      return i18n.t('menu.Hotel', { defaultValue: 'Hotel' });
    }

    if (normalized === 'hoteldashboard') {
      return i18n.t('menu.HotelDashboard', { defaultValue: i18n.t('hotelDashboard.title', { defaultValue: 'Hotel Dashboard' }) });
    }

    if (normalized === 'hotelinfo') {
      return i18n.t('menu.HotelManagement', { defaultValue: 'Hotel Management' });
    }

    if (normalized === 'hotelpricing') {
      return i18n.t('menu.HotelPricing', { defaultValue: 'Hotel Pricing' });
    }

    if (normalized === 'reservation') {
      return i18n.t('menu.Reservation', { defaultValue: 'Reservation' });
    }

    if (normalized === 'bookingrequest') {
      return i18n.t('menu.BookingRequest', { defaultValue: 'Booking Request' });
    }

    if (normalized === 'bookingchannel') {
      return i18n.t('menu.BookingChannel', { defaultValue: 'Booking Channel' });
    }

  if (normalized === 'area') {
    return i18n.t('area.area', { defaultValue: 'Area' });
  }

  if (normalized === 'userplaygroundinventory') {
    return i18n.t('menu.UserPlaygroundInventory', { defaultValue: 'Playground Inventory' });
  }

  return i18n.t(`menu.${raw}`, { defaultValue: raw });
};

  const translateMenuLabel = (raw?: string, source?: string) => {
    const value = (raw || '').trim();
    const normalizedSource = (source || '').trim().toLowerCase().replace(/[\s_-]+/g, '');
    if (!value) return value;

    if (normalizedSource === 'restaurantmap') {
      return i18n.t('menu.RestaurantMap', { defaultValue: 'Restaurant Map' });
    }

    if (normalizedSource === 'companybalance') {
      return i18n.t('menu.CompanyBalance', { defaultValue: i18n.t('companyBalance.title', { defaultValue: 'Company Balance' }) });
    }

if (normalizedSource === 'expenses') {
  return i18n.t('menu.Expenses', {
    defaultValue: i18n.t('expenses.invoicesTitle', {
      defaultValue: 'Expenses',
    }),
  });
}

if (normalizedSource === 'expensecategories') {
  return i18n.t('menu.ExpenseCategories', {
    defaultValue: i18n.t('expenses.categoriesTitle', {
      defaultValue: 'Expense Categories',
    }),
  });
}

if (normalizedSource === 'dailyinventoryclose') {
  return i18n.t('menu.DailyInventoryClose', {
    defaultValue: 'Daily Inventory Close',
  });
}

if (normalizedSource === 'dailyinventory') {
  return i18n.t('menu.DailyInventory', {
    defaultValue: 'Daily Inventory',
  });
}

if (normalizedSource === 'umbreservations') {
  return i18n.t('umbReservations.title', {
    defaultValue: 'Umbrella reservations',
  });
}

if (normalizedSource === 'userplaygroundinventory') {
  return i18n.t('menu.UserPlaygroundInventory', {
    defaultValue: 'Playground Inventory',
  });
}

    const normalized = value.toLowerCase().replace(/[\s_-]+/g, '');
    if (normalized === 'companybalance') {
      return i18n.t('menu.CompanyBalance', { defaultValue: i18n.t('companyBalance.title', { defaultValue: 'Company Balance' }) });
    }

    if (normalized === 'dailyinventoryclose') {
      return i18n.t('menu.DailyInventoryClose', { defaultValue: 'Daily Inventory Close' });
    }

    if (normalized === 'dailyinventory') {
      return i18n.t('menu.DailyInventory', { defaultValue: 'Daily Inventory' });
    }

    if (normalized === 'umbreservations') {
      return i18n.t('umbReservations.title', { defaultValue: 'Umbrella reservations' });
    }

    if (normalized === 'userplaygroundinventory') {
      return i18n.t('menu.UserPlaygroundInventory', { defaultValue: 'Playground Inventory' });
    }

    if (normalized === 'menudetails') {
      return i18n.t('menu.menuDetails', { defaultValue: 'Menu Details' });
    }

    return labelOf(value);
  };

  const companyName = user?.companyName || user?.CompanyName || user?.companyName  || 'Company';
  const companyLogoUrl = buildEntityFileUrl(user?.companyId, ApiConstants.EntityTypes.Company); 
  const companyInitial = (companyName || 'C').trim().charAt(0).toUpperCase();
  const userDisplayName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();
  const userAvatarUrl = buildEntityFileUrl(user?.id, ApiConstants.EntityTypes.User);
  const userInitial = (user?.userName || userDisplayName || 'U').trim().charAt(0).toUpperCase();



  // Funksioni kryesor për të kontrolluar nëse një menu është aktive
  const isActive = (url?: string) => {
    const path = menuUrlToUiRoute(url);
    return location.pathname === path;
  };

  // Funksion për të kontrolluar nëse një submenu është aktive
  const isSubActive = (url?: string) => {
    const path = menuUrlToUiRoute(url);
    return location.pathname === path;
  };

  // Kontrollon nëse ndonjë submenu e grupit është aktive
  const hasActiveSub = (menuItem: IUserMenu) => {
    return (menuItem.subItems || []).some((sub: any) => {
      const subPath = menuUrlToUiRoute(menuRouteSource(sub));
      return location.pathname === subPath;
    });
  };

  const toggleGroup = (id: number) => setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  const handleNavigate = () => {
    onNavigate?.();
  };

  const getIconForMenu = (label: string) => {
    switch (label.toLowerCase()) {
      case 'today':
        return <CottageOutlinedIcon className="lsIcon" />;
      case 'dashboard':
        return <DashboardIcon className="lsIcon" />;
      case 'umbrella':
        return <BeachAccessIcon className="lsIcon" />;
      case 'order':
        return <ShoppingCartIcon className="lsIcon" />;
      case 'kitchen':
        return <KitchenIcon className="lsIcon" />;
      case 'product':
        return <InventoryIcon className="lsIcon" />;
      case 'daily inventory close':
      case 'dailyinventoryclose':
        return <InventoryIcon className="lsIcon" />;
      case 'tables':
        return <TableChartIcon className="lsIcon" />;
      case 'invoicemanagement':
        return <ManageSearchIcon className="lsIcon" />;
      case 'invoices':
        return <ReceiptIcon className="lsIcon" />;
      case 'settings':
        return <SettingsSuggestIcon className="lsIcon" />;
      case 'restaurant':
        return <MenuBookIcon className="lsIcon" />;
      case 'printers':
        return <PrintIcon className="lsIcon" />;
      case 'hotel':
        return <HotelIcon className="lsIcon" />;
      case 'accounting':
        return <AccountBalanceWalletIcon className="lsIcon" />;
      case 'expenses':
      case 'expensecategories':
      case 'expense categories':
        return <AccountBalanceWalletIcon className="lsIcon" />;
      case 'playground':
      case 'playgrounds':
      case 'userplaygroundinventory':
      case 'playground inventory':
        return <PlaygroundMenuIcon />;
      case 'menu':
        return <MenuBookIcon className="lsIcon" />;
      default:
        return <CottageOutlinedIcon className="lsIcon" />;
    }
  };

  if (stage === 0) return null;

  return (
    <div
      className={[
        'lsRoot',
        stage === 1 ? 'isDock isIcons' : '',
        'isDockLeft',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {isFull && (
        <div className="lsTop">
          <div className="lsLogoWrap">
            <div className="lsBrandShell">
              {companyLogoUrl ? (
                <img
                  src={companyLogoUrl}
                  alt={companyName}
                  className="lsLogo"
                  onError={(e) => ((e.currentTarget as HTMLImageElement).style.display = 'none')}
                />
              ) : (
                <div className="lsLogoFallback" aria-hidden="true">
                  {companyInitial}
                </div>
              )}
              <div className="lsBrandBlock">
                <div className="lsBrand" title={companyName}>
                  {companyName}
                </div>
              </div>
            </div>

            <div className="lsLogoActions">
              <NotificationIcon className="lsNotification" iconSize={20} />
            </div>
            <AppPreferenceControls variant="sidebar-language" />

            {stage === 2 && (
              <div className="lsDockToggleStageWrap">
                <button
                  type="button"
                  className={`lsDockPlacementBtn ${dockPlacement === 'left' ? 'isActive' : ''}`}
                  onClick={() => onSetDockPlacement('left')}
                  title={moveMenuLeftLabel}
                  aria-label={moveMenuLeftLabel}
                >
                  <WestOutlinedIcon fontSize="small" />
                </button>
                <button
                  type="button"
                  className={`lsDockPlacementBtn ${dockPlacement === 'top' ? 'isActive' : ''}`}
                  onClick={() => onSetDockPlacement('top')}
                  title={moveMenuTopLabel}
                  aria-label={moveMenuTopLabel}
                >
                  <NorthOutlinedIcon fontSize="small" />
                </button>
                <button type="button" className="lsDockToggleStage" onClick={onToggleMenu} title={menuToggleLabel} aria-label={menuToggleLabel}>
                  <MenuOpenIcon fontSize="small" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <nav className={`lsNav ${isFull ? 'isFull' : ''}`}>
        {stage === 1 && (
          <button type="button" className="lsDockToggle" onClick={onToggleMenu} title={menuToggleLabel} aria-label={menuToggleLabel}>
            <MenuOpenIcon fontSize="small" />
          </button>
        )}

        {items.map((menuItem: IUserMenu) => {
          const hasSubs = (menuItem.subItems?.length || 0) > 0;
          const active = isActive(menuRouteSource(menuItem.item));
          const hasActiveChild = hasActiveSub(menuItem);
          // Grupi hapet nëse është klikuar manualisht OSE nëse ka ndonjë submenu aktive
          const isOpen = !!openGroups[menuItem.item.id];
          const isGroupActive = active || (hasSubs && isOpen) || hasActiveChild;
          const href = menuUrlToUiRoute(menuRouteSource(menuItem.item));

          return (
            <div key={menuItem.item.id} className={`lsGroup ${isFull ? 'isFull' : ''} ${hasSubs ? 'hasSub' : ''}`}>
              <div className={`lsRow ${active ? 'isActive' : ''} ${isGroupActive ? 'isGroupActive' : ''}`}>
                <Link
                  to={href}
                  className="lsLink"
                  title={labelOf(menuItem.item.label)}
                  onClick={(e) => {
                    if (isFull && hasSubs) {
                      e.preventDefault();
                      toggleGroup(menuItem.item.id);
                      return;
                    }

                    handleNavigate();
                  }}
                >
                  <span className="lsIconWrap">{getIconForMenu(menuItem.item.label)}</span>
                  {!isIconsOnly && <span className="lsText">{labelOf(menuItem.item.label)}</span>}
                </Link>

                {isFull && hasSubs && (
                  <button
                    type="button"
                    className={`lsCollapseBtn ${isOpen ? 'isOpen' : ''}`}
                    onClick={() => toggleGroup(menuItem.item.id)}
                    aria-label="Toggle submenu"
                    title="Toggle submenu"
                  >
                    ▾
                  </button>
                )}
              </div>

              {isFull && hasSubs && (
                <div className={`lsSub ${isOpen ? 'isOpen' : ''}`}>
                  {(() => {
                    const isSettings = (menuItem.item.label || '').toLowerCase() === 'settings';

                    const subs = isSettings
                      ? [...menuItem.subItems]
                      : (menuItem.item.label || '').toLowerCase() === 'hotel'
                        ? (menuItem.subItems || []).filter((sub: any) => {
                            const subPath = menuUrlToUiRoute(menuRouteSource(sub));
                            const subLabel = (sub.label || '').toLowerCase().replace(/[\s_-]+/g, '');
                            return subPath !== '/roomtypes' && subPath !== '/hotelmanagement' && subLabel !== 'hotelmanagement';
                          })
                        : menuItem.subItems;

                    return subs.map((sub: any) => {
                      const subHref = menuUrlToUiRoute(menuRouteSource(sub));
                      const title = translateMenuLabel(sub.label, menuRouteSource(sub));
                      // Përdor isSubActive për të kontrolluar nëse submenu është aktive
                      const isSubActiveFlag = isSubActive(menuRouteSource(sub));

                      return (
                        <Link
                          key={sub.id ?? sub.url}
                          to={subHref}
                          className={`lsSubLink ${isSubActiveFlag ? 'isActive' : ''}`}
                          title={title}
                          onClick={handleNavigate}
                        >
                          {title}
                        </Link>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
          );
        })}

        {isFull && (
          <AppPreferenceControls variant="sidebar-theme" />
        )}
      </nav>

      {isFull && user && (
        <div className="lsBottom">
          <div className="lsUser">
            {userAvatarUrl ? (
              <img src={userAvatarUrl} alt={user.userName || 'User'} className="lsAvatar" />
            ) : (
              <div className="lsAvatar lsAvatarFallback" aria-hidden="true">
                {userInitial}
              </div>
            )}
            <div className="lsUserMeta">
              <div className="lsUserName" title={user.userName}>
                {user.userName}
              </div>
              <div className="lsUserRole" title={`${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()}>
                {user.firstName} {user.lastName}
              </div>
            </div>
            <div className="lsUserActions">
              <AppPreferenceControls variant="sidebar-language" sidebarLanguagePlacement="bottom" />

              <button className="lsLogoutBtn" onClick={handleLogout} type="button" title={i18n.t('generic.logout')}>
                <Logout fontSize="small" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeftSection;

